import { AnalyticsEvent, captureEvent } from '../helpers/analytics'
import { Platform } from 'react-native'
import { useAppSelector } from '../state/hooks'

export const useTrackGoogleAnalytics = () => {
	const { currency } = useAppSelector(state => state.settings)
	const trackEvent = (name: AnalyticsEvent, params: any) => {
		if (Platform.OS === 'web') {
			return captureEvent(name, params, currency as 'usd' | 'cad')
		}
		return Promise.resolve()
	}

	// Convenience methods for common events
	const trackAddToCart = (params: {
		itemId: string
		itemName: string
		price: number
		quantity: number
		options?: {
			itemCategory?: string
			itemLocationId?: string
			itemBrand?: string
			itemListId?: string
			itemListName?: string
		}
	}) => {
		return trackEvent(AnalyticsEvent.AddToCart, params)
	}

	const trackBeginCheckout = (params: {
		cart: Array<{
			id: string
			name: string
			qty: number
			amount: number
			rId: string
			menuItemCategory: string
		}>
	}) => {
		return trackEvent(AnalyticsEvent.BeginCheckout, { cart: params.cart })
	}

	const trackPurchase = (params: {
		orderId: string
		cart: Array<{
			id: string
			name: string
			qty: number
			amount: number
			rId: string
			menuItemCategory: string
		}>
		options?: {
			couponCode?: string
			tax?: number
			restaurantData: Record<string, string>
		}
	}) => {
		return trackEvent(AnalyticsEvent.Purchase, params)
	}

	const trackViewMenu = (params: { restaurantId: string; restaurantName: string }) => {
		return trackEvent(AnalyticsEvent.ViewMenu, params)
	}

	const trackViewItem = (params: {
		itemId: string
		itemName: string
		itemPrice: number
		options?: {
			itemBrand?: string
			itemLocationId?: string
		}
	}) => {
		return trackEvent(AnalyticsEvent.ViewItem, params)
	}

	const trackRemoveItem = (params: {
		itemId: string
		itemName: string
		price: number
		quantity: number
	}) => {
		return trackEvent(AnalyticsEvent.RemoveItem, params)
	}

	const trackTipSelection = (tipAmount: number, tipPercentage: number) => {
		return trackEvent(AnalyticsEvent.TipSelection, {
			tipAmount,
			tipPercentage,
		})
	}

	const trackBasicInfo = (step: string) => {
		return trackEvent(AnalyticsEvent.BasicInfo, { step })
	}

	const trackReviewOrder = () => {
		return trackEvent(AnalyticsEvent.ReviewOrder, {})
	}

	const trackPayment = (paymentMethod: string) => {
		return trackEvent(AnalyticsEvent.Payment, { paymentMethod })
	}

	return {
		trackEvent,
		trackAddToCart,
		trackBeginCheckout,
		trackPurchase,
		trackViewMenu,
		trackViewItem,
		trackRemoveItem,
		trackTipSelection,
		trackBasicInfo,
		trackReviewOrder,
		trackPayment,
	}
}
